<template>
  <v-tooltip
    location="bottom"
    :open-delay="200"
    :disabled="compact || (!item.statemessage && !statusStateMessage)"
    :aria-hidden="tooltipLabel.length === 0"
    :aria-label="tooltipLabel"
  >
    <template v-slot:activator="{ props }">
      <div class="pod-thumbnail" v-bind="props">
        <div
          v-if="imageType === 'livePreview'"
          class="live-preview"
        >
          <div
            ref="livePreview"
            :class="{ 'mx-auto': true, 'image': true, 'compact': compact }"
            @mousemove.capture="moveLivePreview"
            @mouseout="resetLivePreview"
            :style="{ backgroundImage: `url('${livePreviewImage}')` }"
          >
            <div class="cursor" style="display: none"/>
          </div>
        </div>
        <div
          v-else-if="animatedThumbnail"
          :class="{
        'mx-auto': true,
        'elevation-1': true,
        'preview-image': true,
        'animated-preview-image': true,
        'compact': compact,
      }"
        >
          <div
            :key="animatedThumbnail.key"
            class="sprite-image"
            :style="animatedThumbnailStyle"
          />
        </div>
        <div
          v-else
          :class="{
        'mx-auto': true,
        'elevation-1': true,
        'preview-image': true,
        'compact': compact,
        ...additionalImageClasses
      }"
          :style="{ backgroundImage: `url('${imageUrl}')` }"
        />
      </div>
    </template>

    <div v-if="item.statemessage">
      <span v-if="typeof item.statemessage == 'object'">
        {{ $replaceT(item.statemessage.code, item.statemessage.params, item.statemessage.content) }}
      </span>
      <span v-else>
        {{ item.statemessage }}
      </span>
    </div>
    <!-- In case the state message wasn't set, compute one based on current pod status -->
    <div v-else-if="!compact && statusStateMessage">
      {{ $replaceT(statusStateMessage.code, statusStateMessage.params) }}
    </div>
  </v-tooltip>
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js';
import { useMainStore } from '@/stores/MainStore.js';
import { usePodStore } from '@/stores/PodStore.js';
import uploadingDark from '@/assets/images/uploading-dark.webp';
import uploadingLight from '@/assets/images/uploading-light.webp';
import transcodingDark from '@/assets/images/transcoding-dark.webp';
import transcodingLight from '@/assets/images/transcoding-light.webp';
import zipArchiveThumbnailDark from '@/assets/images/thumbnail-ext/zip-archive-dark.webp';
import zipArchiveThumbnailLight from '@/assets/images/thumbnail-ext/zip-archive-light.webp';
import rarArchiveThumbnailDark from '@/assets/images/thumbnail-ext/rar-archive-dark.webp';
import rarArchiveThumbnailLight from '@/assets/images/thumbnail-ext/rar-archive-light.webp';
import tarArchiveThumbnailDark from '@/assets/images/thumbnail-ext/tar-archive-dark.webp';
import tarArchiveThumbnailLight from '@/assets/images/thumbnail-ext/tar-archive-light.webp';
import sevenZArchiveThumbnailDark from '@/assets/images/thumbnail-ext/7zip-archive-dark.webp';
import sevenZArchiveThumbnailLight from '@/assets/images/thumbnail-ext/7zip-archive-light.webp';
import gzipArchiveThumbnailDark from '@/assets/images/thumbnail-ext/gzip-archive-dark.webp';
import gzipArchiveThumbnailLight from '@/assets/images/thumbnail-ext/gzip-archive-light.webp';
import docxThumbnailDark from '@/assets/images/thumbnail-ext/docx-dark.webp';
import docxThumbnailLight from '@/assets/images/thumbnail-ext/docx-light.webp';
import pdfThumbnailDark from '@/assets/images/thumbnail-ext/pdf-dark.webp';
import pdfThumbnailLight from '@/assets/images/thumbnail-ext/pdf-light.webp';
import txtThumbnailDark from '@/assets/images/thumbnail-ext/txt-dark.webp';
import txtThumbnailLight from '@/assets/images/thumbnail-ext/txt-light.webp';
import xlsxThumbnailDark from '@/assets/images/thumbnail-ext/xlsx-dark.webp';
import xlsxThumbnailLight from '@/assets/images/thumbnail-ext/xlsx-light.webp';
import imageBroken from '@/assets/images/image-broken.png';
import noThumbnailImage from '@/assets/images/no-thumbnail-small.png';
import audioThumbnail from '@/assets/images/audio-thumbnail-small.png';
import MimetypeHelper from '@/components/MimetypeHelper.js';

export default {
  name: 'PodThumbnail',
  props: {
    item: {
      type: Object,
      required: true
    },
    compact: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  data() {
    return {
      livePreviewImage: '',
    };
  },
  mounted() {
    if (this.imageType === 'livePreview') {
      this.refreshLivePreviewImage();
    }
  },
  methods: {
    moveLivePreview(e) {
      const x = e.offsetX;
      const totalFrames = 140;
      const pixelsPerFrame = this.boxSize / totalFrames;

      let currentFrame = Math.floor(x / pixelsPerFrame);
      if (currentFrame >= totalFrames) {
        currentFrame = totalFrames - 1;
      } else if (currentFrame < 0) {
        currentFrame = 0;
      }

      const positionX = currentFrame * this.boxSize;
      this.$refs.livePreview.style['background-position-x'] = `${-positionX}px`;
      this.$refs.livePreview.firstChild.style['left'] = `${x}px`;
      this.$refs.livePreview.firstChild.style['display'] = '';
    },
    resetLivePreview() {
      const totalFrames = 140;
      const currentFrame = Math.ceil(totalFrames / 2);

      this.$refs.livePreview.style['background-position-x'] = `${-currentFrame * this.boxSize}px`;
      this.$refs.livePreview.firstChild.style['display'] = 'none';
    },
    refreshLivePreviewImage() {
      if (!this.isBroken && this.livePreviewAsset) {
        this.livePreviewImage = this.livePreviewAsset.url;
        this.resetLivePreview();
      }
    },
  },
  watch: {
    imageType(newType) {
      if (newType === 'livePreview') {
        this.$nextTick(() => this.refreshLivePreviewImage());
      }
    }
  },
  computed: {
    tooltipLabel() {
      if (this.item.statemessage) {
        if (typeof this.item.statemessage == 'object') {
          return this.$replaceT(this.item.statemessage.code, this.item.statemessage.params, this.item.statemessage.content);
        }

        return this.item.statemessage;
      }

      if (!this.compact && this.statusStateMessage) {
        return this.$replaceT(this.statusStateMessage.code, this.statusStateMessage.params);
      }

      return '';
    },

    boxSize() {
      return this.compact ? 140 : 200;
    },
    getDarkMode() {
      const mainStore = useMainStore();
      return mainStore.getDarkMode;
    },
    archiveThumbnail() {
      switch (this.rawAssetFileType) {
        case 'rar':
          return this.getDarkMode ? rarArchiveThumbnailDark : rarArchiveThumbnailLight;
        case 'tar':
          return this.getDarkMode ? tarArchiveThumbnailDark : tarArchiveThumbnailLight;
        case '7z':
          return this.getDarkMode ? sevenZArchiveThumbnailDark : sevenZArchiveThumbnailLight;
        case 'gz':
          return this.getDarkMode ? gzipArchiveThumbnailDark : gzipArchiveThumbnailLight;
        default:
          // Act like a backup option
          return this.getDarkMode ? zipArchiveThumbnailDark : zipArchiveThumbnailLight;
      }
    },
    isBroken() {
      if (!this.item.asset) {
        return false;
      }

      for (const assetKey in this.item.asset) {
        for (const asset of this.item.asset[assetKey]) {
          if (asset.broken) return true;
        }
      }

      return false;
    },
    isAudioItem() {
      return MimetypeHelper.isMimetypeAudio(this.item);
    },
    isArchiveItem() {
      return MimetypeHelper.isMimetypeArchive(this.item);
    },
    isPdfItem() {
      return MimetypeHelper.isPdfAsset(this.item?.asset?.raw?.[0]);
    },
    isDocxItem() {
      return MimetypeHelper.isDocxAsset(this.item?.asset?.raw?.[0]);
    },
    isXlsxItem() {
      return MimetypeHelper.isXlsxAsset(this.item?.asset?.raw?.[0]);
    },
    isTxtItem() {
      return MimetypeHelper.isTxtAsset(this.item?.asset?.raw?.[0]);
    },
    podState() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    assetState() {
      const commonStore = useCommonStore();
      return commonStore.assetStates;
    },
    livePreviewAsset() {
      if (this.item.asset && this.item.asset.livepreview && this.item.asset.livepreview.length > 0) {
        return this.item.asset.livepreview.find((val) => val.state === this.assetState.COMPLETE);
      }

      return null;
    },
    thumbnailAsset() {
      if (this.item.asset && this.item.asset.thumbnail && this.item.asset.thumbnail.length > 0) {
        return this.item.asset.thumbnail.find((val) => val.state === this.assetState.COMPLETE);
      }

      return null;
    },
    animatedThumbnail() {
      switch (this.animatedThumbnailType) {
        case 'spinner':
          return {
            key: `spinner-${this.getDarkMode ? 'dark' : 'light'}`,
            url: this.getDarkMode ? uploadingDark : uploadingLight,
            frameCount: 48,
            duration: '2s',
            frameSize: 100,
            sheetWidth: 4800,
            startX: 0,
            endX: -4700,
          };
        case 'shutter':
          return {
            key: `shutter-${this.getDarkMode ? 'dark' : 'light'}`,
            url: this.getDarkMode ? transcodingDark : transcodingLight,
            frameCount: 144,
            duration: '6s',
            frameSize: 100,
            sheetWidth: 14400,
            startX: 0,
            endX: -14300,
          };
        default:
          return null;
      }
    },
    animatedThumbnailType() {
      if (this.imageType === 'shutter') {
        return 'shutter';
      }

      if (this.imageType !== 'spinner') {
        return this.imageType;
      }

      const status = this.item?.status?.status;
      if (!status) {
        return 'spinner';
      }

      if (['completed', 'upload_in_progress', 'upload_failed', 'queued'].includes(status)) {
        return 'spinner';
      }

      return 'shutter';
    },
    animatedThumbnailStyle() {
      if (!this.animatedThumbnail) {
        return {};
      }

      const displayScale = this.compact ? 0.8 : 1.12;
      const frameSize = this.animatedThumbnail.frameSize;

      return {
        '--sprite-start-x': `${this.animatedThumbnail.startX}px`,
        '--sprite-end-x': `${this.animatedThumbnail.endX}px`,
        animationDuration: this.animatedThumbnail.duration,
        animationTimingFunction: `steps(${this.animatedThumbnail.frameCount}, jump-none)`,
        backgroundImage: `url('${this.animatedThumbnail.url}')`,
        backgroundSize: `${this.animatedThumbnail.sheetWidth}px ${frameSize}px`,
        backgroundPositionX: `${this.animatedThumbnail.startX}px`,
        height: `${frameSize}px`,
        transform: `scale(${displayScale})`,
        width: `${frameSize}px`,
      };
    },
    imageUrl() {
      switch (this.imageType) {
        case 'broken':
          return imageBroken;
        case 'livePreview':
          return this.livePreviewAsset.url;
        case 'thumbnail':
          return this.thumbnailAsset.url;
        case 'audio':
          return audioThumbnail;
        case 'archive':
          return this.archiveThumbnail;
        case 'pdf':
          return this.getDarkMode ? pdfThumbnailDark : pdfThumbnailLight;
        case 'docx':
          return this.getDarkMode ? docxThumbnailDark : docxThumbnailLight;
        case 'xlsx':
          return this.getDarkMode ? xlsxThumbnailDark : xlsxThumbnailLight;
        case 'text':
          return this.getDarkMode ? txtThumbnailDark : txtThumbnailLight;
        default:
          return noThumbnailImage;
      }
    },
    imageType() {
      if (this.isBroken) {
        return 'broken';
      }

      switch (this.item.state) {
        case this.podState.ERROR:
          return 'imageBroken';
        case this.podState.PENDING:
          return 'spinner';
        case this.podState.PROCESSING:
          return 'shutter';
        case this.podState.COMPLETE:
          if (this.livePreviewAsset) {
            return 'livePreview';
          } else if (this.thumbnailAsset) {
            return 'thumbnail';
          } else if (this.isAudioItem) {
            return 'audio';
          } else if (this.isArchiveItem) {
            return 'archive';
          } else if (this.isPdfItem) {
            return 'pdf';
          } else if (this.isDocxItem) {
            return 'docx';
          } else if (this.isXlsxItem) {
            return 'xlsx';
          } else if (this.isTxtItem) {
            return 'text';
          }

          return 'noThumbnail';
        default:
          return 'noThumbnail';
      }
    },
    rawAssetFileType() {
      if (this.item.asset.raw) {
        return this.item.asset.raw[0].filetype;
      }

      return null;
    },
    additionalImageClasses() {
      if (this.isBroken || ['spinner', 'shutter'].includes(this.imageType)) {
        return {
          'background-contain': true,
        };
      }

      if (this.isArchiveItem || this.isPdfItem || this.isTxtItem || this.isDocxItem || this.isXlsxItem) {
        return {
          'ext-thumbnail': true
        };
      }

      return {};
    },
    statusStateMessage() {
      if (this.item && this.item.status && this.item.status.status !== 'completed') {
        const podStore = usePodStore();
        return podStore.getStatusStateMessage(this.item.id);
      }

      return null;
    },
  }
};
</script>

<style lang="scss" scoped>
.pod-thumbnail {
  --image-width: 200px;
  --image-height: 112px;
  --compact-image-width: 120px;
  --compact-image-height: 80px;

  & .live-preview {
    width: 100%;
    height: 100%;
    background-size: contain;
    background-position: center;
    background-image: url("@/assets/images/tw-transparent.png");

    & > .image {
      &.compact {
        width: var(--compact-image-width);
        height: var(--compact-image-height);
        background-size: 19600px var(--compact-image-height);
      }

      cursor: ew-resize;
      width: var(--image-width);
      height: var(--image-height);
      background-size: 28000px var(--image-height);

      & > .cursor {
        position: relative;
        height: 100%;
        width: 0;
        border: 1px solid #ff0000;
        pointer-events: none;
      }
    }
  }

  & .preview-image {
    display: block;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    width: var(--image-width);
    height: var(--image-height);

    &.compact {
      width: var(--compact-image-width);
      height: var(--compact-image-height);
    }
  }

  & .animated-preview-image {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .sprite-image {
    width: 100px;
    height: 100px;
    background-repeat: no-repeat;
    animation-name: pod-thumbnail-sprite;
    animation-iteration-count: infinite;
    transform-origin: center;
  }

  & .background-contain {
    background-size: contain;
  }

  & .ext-thumbnail {
    background-size: 90px;

    &.compact {
      background-size: 70px;
    }
  }
}

@keyframes pod-thumbnail-sprite {
  from {
    background-position-x: var(--sprite-start-x);
  }

  to {
    background-position-x: var(--sprite-end-x);
  }
}
</style>
